// node_modules
import {Dropdown} from 'primereact/dropdown'
import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
	useCreateOrganizationMutation, useDeleteOrganizationMutation,
	useGetOrganizationListQuery, useUpdateOrganizationMutation
} from '../../../redux/services/organization'

// redux
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

// context
import { ToastContext } from "../../../App";

// styles
import * as styles from "./Organizations.module.css";

const Organizations = () => {
	const showToast = useContext(ToastContext);
	const dispatch = useDispatch();
	
	const { control, setValue, handleSubmit } = useForm({
		defaultValues: {
			name: "",
			subdomain: "",
			timezone: ""
		},
	});
	
	const {
		data: organizations,
		isSuccess: isGetOrganizationsSuccess,
		isLoading: isGetOrganizationsLoading,
		isError: isGetOrganizationsError
	} = useGetOrganizationListQuery();
	const [createOrganization, { isSuccess: isCreateOrganizationSuccess, isError: isCreateOrganizationError, error: createOrganizationError }] =
		useCreateOrganizationMutation();
	const [deleteOrganization, {isSuccess: isDeleteOrganizationSuccess, isError: isDeleteOrganizationError, error: deleteOrganizationError }]
		= useDeleteOrganizationMutation();
	const [updateOrganization, { isSuccess: isUpdateOrganizationSuccess, isError: isUpdateOrganizationError, error: updateOrganizationError }] =
		useUpdateOrganizationMutation();
	
	const [id, setId] = useState(null);
	const [name, setName] = useState(null);
	const [mode, setMode] = useState("create");
	const [showModal, setShowModal] = useState(false);
	
	const openModal = (mode, rowData) => () => {
		if (mode === "create") {
			setId(null);
			setValue("name", "");
			setValue("timezone", "");
			setValue("subdomain", "");
		} else {
			setId(rowData.id);
			setValue("name", rowData.name);
			setValue("timezone", rowData.timezone);
			setValue("subdomain", rowData.subdomain);
		}
		setShowModal(true);
		setMode(mode);
	};
	
	const onCreate = async (data) => {
		const { name, subdomain, timezone } = data;
		
		const payload = {
			name,
			subdomain,
			timezone
		};
		
		createOrganization(payload);
	};
	
	const onSaveEdit = async (data) => {
		const { name, subdomain, timezone } = data;
		
		const payload = {
			name,
			subdomain,
			timezone
		};
		
		updateOrganization({
			id: id,
			payload,
		});
	};
	
	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			if (mode === "create") {
				handleSubmit(onCreate)();
			} else {
				handleSubmit(onSaveEdit)();
			}
		}
	};
	
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	
	useEffect(() => {
		if(isGetOrganizationsSuccess)
			dispatch(setLoading(false));
	}, [isGetOrganizationsSuccess])
	
	useEffect(() => {
		if(isGetOrganizationsError) {
			showToast('error', 'Organization', 'Unable to load organizations')
			dispatch(setLoading(false));
		}
	}, [isGetOrganizationsError])
	
	useEffect(() => {
		if(isCreateOrganizationSuccess) {
			showToast('success', 'Organization', 'Organization created!')
			setShowModal(false);
		}
	}, [isCreateOrganizationSuccess])
	
	useEffect(() => {
		if(isCreateOrganizationError)
			showToast('error', createOrganizationError?.status, createOrganizationError?.data?.message || (createOrganizationError?.data?.errors && "Incorrect data"))
	}, [isCreateOrganizationError])
	
	useEffect(() => {
		if(isUpdateOrganizationSuccess) {
			showToast('success', 'Organization', 'Organization updated!')
			setShowModal(false);
		}
	}, [isUpdateOrganizationSuccess])
	
	useEffect(() => {
		if(isUpdateOrganizationError) {
			showToast("error", updateOrganizationError?.status, updateOrganizationError?.data?.message || (updateOrganizationError?.data?.errors && "Incorrect data"))
		}
	}, [isUpdateOrganizationError])
	
	useEffect(() => {
		if(isDeleteOrganizationSuccess) {
			showToast('success', 'Organization', 'Organization deleted!')
			setShowDeleteModal(false)
		}
	}, [isDeleteOrganizationSuccess]);
	
	useEffect(() => {
		if(isDeleteOrganizationError) {
			showToast('error', deleteOrganizationError?.status, deleteOrganizationError?.data?.message || (deleteOrganizationError?.data?.errors && "Incorrect data"));
		}
	}, [isDeleteOrganizationError])
	
	const openDeleteModal = (rowData) => () => {
		setId(rowData.id)
		setName(rowData.name)
		setShowDeleteModal(true)
	}
	
	const onDelete = () => {
		deleteOrganization(id)
	}
	
	const actionBodyTemplate = (rowData) => {
		return (
			<div className={styles.cellSoundWrapper}>
				<Button
					icon="pi pi-pencil"
					className={`p-button-text ${styles.soundBtn}`}
					onClick={openModal('edit', rowData)}
				/>
				<Button
					icon="pi pi-trash"
					className={`p-button-text ${styles.deleteBtn}`}
					onClick={openDeleteModal(rowData)}
				/>
			</div>
		);
	};
	
	return (
		<div className={styles.pageWrapper}>
			<div
				className={`flex justify-content-between align-items-center ${styles.header}`}
			>
				<h2>Organizations</h2>
				<Button className={styles.createBtn} onClick={openModal("create")}>
					ADD ORGANIZATIONS <i className="pi pi-plus" />
				</Button>
			</div>
			{organizations && (
				<DataTable value={organizations} rows={6} loading={isGetOrganizationsLoading}>
					<Column
						field="name"
						header="Name"
						body={(rowData) => {
							return <div className={styles.cellInsider}>{rowData.name}</div>;
						}}
					/>
					
					<Column
						field="subdomain"
						header="Subdomain"
						style={{ width: "175px" }}
						body={(rowData) => {
							return <div className={styles.cellInsider}>{rowData.subdomain}</div>;
						}}
					/>
					
					<Column
						field="timezone"
						header="Timezone"
						style={{ width: "175px" }}
						body={(rowData) => {
							return <div className={styles.cellInsider}>{rowData.timezone}</div>;
						}}
					/>
					
					<Column
						body={actionBodyTemplate}
						header="Actions"
						style={{ width: "175px" }}
					/>
				</DataTable>
			)}
			
			<Dialog
				header={`${mode === "create" ? "New" : "Edit"} Organization`}
				visible={showModal}
				onHide={() => {
					setShowModal(false);
				}}
				footer={() => (
					<div className="flex gap-2 justify-content-end">
						<Button
							label="Save"
							type="submit"
							className={`p-button-success ${styles.saveBtn}`}
							onClick={
								mode === "create" ? handleSubmit(onCreate) : handleSubmit(onSaveEdit)
							}
						/>
					</div>
				)}
				className={`p-fluid ${styles.organizationModal}`}
			>
				<form>
							<Controller
								name="name"
								control={control}
								render={({ field }) => (
									<div className="field">
										<label>Name</label>
										<InputText
											className={`w-full mb-3 ${styles.input}`}
											onKeyDown={handleKeyDown}
											placeholder="Name"
											{...field}
										/>
									</div>
								)}
							/>
							
							<Controller
								name="subdomain"
								control={control}
								render={({ field }) => (
									<div className="field">
										<label>Subdomain</label>
										<InputText
											className={`w-full mb-3 ${styles.input}`}
											onKeyDown={handleKeyDown}
											placeholder="Subdomain"
											{...field}
										/>
									</div>
								)}
							/>
					
						<Controller
							name="timezone"
							control={control}
							render={({ field }) => (
								<div className={`field ${styles.dropDownWrapper}`}>
									<label>Timezone</label>
									<Dropdown
										id={field.name}
										value={field.value}
										onChange={(e) => field.onChange(e.value)}
										placeholder="Select timezone"
										className="w-full"
										panelClassName={styles.dropDownPanel}
										options={[
											{ label: "UTC-12:00", value: "-12:00" },
											{ label: "UTC-11:00", value: "-11:00" },
											{ label: "UTC-10:00", value: "-10:00" },
											{ label: "UTC-09:30", value: "-09:30" },
											{ label: "UTC-09:00", value: "-09:00" },
											{ label: "UTC-08:00", value: "-08:00" },
											{ label: "UTC-07:00", value: "-07:00" },
											{ label: "UTC-06:00", value: "-06:00" },
											{ label: "UTC-05:00", value: "-05:00" },
											{ label: "UTC-04:00", value: "-04:00" },
											{ label: "UTC-03:30", value: "-03:30" },
											{ label: "UTC-03:00", value: "-03:00" },
											{ label: "UTC-02:00", value: "-02:00" },
											{ label: "UTC-01:00", value: "-01:00" },
											{ label: "UTC+00:00", value: "+00:00" },
											{ label: "UTC+01:00", value: "+01:00" },
											{ label: "UTC+02:00", value: "+02:00" },
											{ label: "UTC+03:00", value: "+03:00" },
											{ label: "UTC+03:30", value: "+03:30" },
											{ label: "UTC+04:00", value: "+04:00" },
											{ label: "UTC+04:30", value: "+04:30" },
											{ label: "UTC+05:00", value: "+05:00" },
											{ label: "UTC+05:30", value: "+05:30" },
											{ label: "UTC+05:45", value: "+05:45" },
											{ label: "UTC+06:00", value: "+06:00" },
											{ label: "UTC+06:30", value: "+06:30" },
											{ label: "UTC+07:00", value: "+07:00" },
											{ label: "UTC+08:00", value: "+08:00" },
											{ label: "UTC+08:45", value: "+08:45" },
											{ label: "UTC+09:00", value: "+09:00" },
											{ label: "UTC+09:30", value: "+09:30" },
											{ label: "UTC+10:00", value: "+10:00" },
											{ label: "UTC+10:30", value: "+10:30" },
											{ label: "UTC+11:00", value: "+11:00" },
											{ label: "UTC+12:00", value: "+12:00" },
											{ label: "UTC+12:45", value: "+12:45" },
											{ label: "UTC+13:00", value: "+13:00" },
											{ label: "UTC+14:00", value: "+14:00" },
										]}
									/>
								</div>
							)}
						/>
				</form>
			</Dialog>
			
			<Dialog
				closable={false}
				header={
					<div style={{ width: '100%', textAlign: 'center' }}>
						Are You Sure?
					</div>}
				visible={showDeleteModal}
				onHide={() => {
					setShowDeleteModal(false);
				}}
				footer={() => (
					<div className="flex flex-column gap-2 justify-content-end">
						<Button
							label="Delete"
							className={`p-button-success ${styles.deleteDialogBtn}`}
							onClick={onDelete}
						/>
						<Button
							label="Cancel"
							text
							className={`p-button-success ${styles.cancelDialogBtn}`}
							onClick={() => {setShowDeleteModal(false)}}
						/>
					</div>
				)}
				className={`p-fluid ${styles.deleteModal}`}
			>
				<div className={styles.text}>
					<span>You want to delete the organization: <strong>{name}</strong>.</span>
					Confirm this action by pressing ‘Delete’ or cancel it.
				</div>
			</Dialog>
		</div>
	);
};

export default Organizations;
